<template>
  <div>
    <div
      v-if="loading"
      key="poy-details-loading"
      class="text-center py-3"
    >
      <b-spinner :label="$t('poyDetailsDocs.loading')" />
    </div>
    <b-alert
      v-else-if="loadFailed"
      show
      variant="danger"
    >
      <p>{{ $t('poyDetailsDocs.loadFailed') }}</p>
      <b-button
        variant="outline-danger"
        size="sm"
        @click="loadDocuments"
      >
        {{ $t('poyDetailsDocs.retry') }}
      </b-button>
    </b-alert>
    <template v-else-if="documents">
      <b-card
        v-if="documents.callback"
        key="poy-details-callback"
        :title="documents.callback.title"
      >
        <p>{{ documents.callback.description }}</p>
        <p>{{ documents.callback.signature }}</p>
        <div class="d-flex justify-content-between align-items-center mb-1">
          <h5 class="mb-0">
            {{ documents.callback.example }}
          </h5>
          <b-button
            v-clipboard:copy="documents.callback.request"
            v-clipboard:success="onCopy"
            v-clipboard:error="onCopyError"
            variant="outline-secondary"
            size="sm"
          >{{ $t('poyDetailsDocs.copy') }}</b-button>
        </div>
        <prism
          language="json"
          class="rounded"
        >
          {{ documents.callback.request }}
        </prism>
      </b-card>

      <b-card
        v-if="documents.api"
        key="poy-details-api"
        :title="documents.api.title"
      >
        <p><code>GET /seamless/poy/detail</code></p>
        <p>{{ documents.api.identifiers }}</p>
        <p>{{ documents.api.signatureDescription }}</p>
        <p>{{ documents.api.secret }}</p>
        <b-alert
          show
          variant="secondary"
        >
          <div class="alert-body text-body font-weight-normal">
            {{ documents.api.retention }}
          </div>
        </b-alert>
        <div
          v-for="sample in apiSamples"
          :key="sample.key"
          class="mb-2"
        >
          <div class="d-flex justify-content-between align-items-center mb-1">
            <h5 class="mb-0">
              {{ documents.api.sampleTitles[sample.key] }}
            </h5>
            <b-button
              v-clipboard:copy="documents.api[sample.key]"
              v-clipboard:success="onCopy"
              v-clipboard:error="onCopyError"
              variant="outline-secondary"
              size="sm"
            >{{ $t('poyDetailsDocs.copy') }}</b-button>
          </div>
          <prism
            :language="sample.language"
            class="rounded"
          >
            {{ documents.api[sample.key] }}
          </prism>
        </div>
        <h5>{{ documents.api.errorsTitle }}</h5>
        <b-table
          :items="errorRows"
          :fields="errorFields"
          small
          responsive
          bordered
        />
        <p class="mb-0">
          {{ documents.api.httpErrors }}
        </p>
      </b-card>

      <b-card :title="documents.common.betsTitle">
        <b-table
          :items="betFields"
          :fields="tableFields"
          small
          responsive
          bordered
        />
        <p>{{ documents.common.numbers }}</p>
        <h5>{{ documents.common.typesTitle }}</h5>
        <b-table
          :items="betTypes"
          :fields="typeFields"
          small
          responsive
          bordered
        />
        <p>{{ documents.common.sets }}</p>
        <template v-if="documents.api">
          <h5>{{ documents.api.statusTitle }}</h5>
          <p>{{ documents.api.status }}</p>
          <p>{{ documents.api.amounts }}</p>
          <p class="mb-0">
            {{ documents.api.time }}
          </p>
        </template>
      </b-card>
    </template>
  </div>
</template>

<script>
import {
  BAlert, BButton, BCard, BSpinner, BTable,
} from 'bootstrap-vue'
import Prism from 'vue-prism-component'
import 'prismjs'
import 'prismjs/components/prism-json'
import 'prismjs/components/prism-bash'
import 'prismjs/themes/prism-tomorrow.css'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import agentService from '@/service/agentService'

export default {
  components: {
    BAlert, BButton, BCard, BSpinner, BTable, Prism,
  },
  data() {
    return {
      loading: true,
      loadFailed: false,
      documents: null,
      requestSequence: 0,
      apiSamples: [
        { key: 'request', language: 'json' },
        { key: 'signature', language: 'javascript' },
        { key: 'curl', language: 'bash' },
        { key: 'response', language: 'json' },
      ],
    }
  },
  computed: {
    tableFields() {
      return [
        { key: 'field', label: this.documents.common.field },
        { key: 'description', label: this.documents.common.description },
      ]
    },
    betFields() {
      return ['type', 'typeName', 'number', 'amount', 'payRate', 'quantity', 'status', 'winAmount']
        .map(field => ({ field, description: this.documents.common.fields[field] }))
    },
    errorFields() {
      return [
        { key: 'code', label: 'status.code' },
        { key: 'description', label: this.documents.common.description },
      ]
    },
    errorRows() {
      return this.documents.api.errors
    },
    typeFields() {
      return [
        { key: 'type', label: 'type' },
        { key: 'lottery', label: this.documents.common.lottery },
        { key: 'yeekee', label: this.documents.common.yeekee },
      ]
    },
    betTypes() {
      const lottery = ['threeTop', 'threeTod', 'threeFront', 'threeLast', 'twoTop', 'twoBottom', 'runTop', 'runBottom', 'set']
      const yeekee = ['threeTop', 'threeTod', 'twoTop', 'twoBottom', 'runTop', 'runBottom']
      // 4/5 ตัว (41/42/51/52) หวยและยี่กีใช้รหัสเดียวกัน · เอกสารจาก agent-service รุ่นเก่าที่ยังไม่มีป้ายชื่อ = ไม่แสดงแถว
      const fourFiveDigit = [[41, 'fourTop'], [42, 'fourTod'], [51, 'fiveTop'], [52, 'fiveTod']]
        .filter(([, label]) => this.documents.common.types[label])
        .map(([type, label]) => ({ type, lottery: this.documents.common.types[label], yeekee: this.documents.common.types[label] }))
      return lottery.map((label, index) => ({
        type: index + 1,
        lottery: this.documents.common.types[label],
        yeekee: yeekee[index] ? this.documents.common.types[yeekee[index]] : '—',
      })).concat(fourFiveDigit)
    },
  },
  watch: {
    '$i18n.locale': 'loadDocuments',
    '$store.state.app.userData': 'loadDocuments',
  },
  mounted() {
    this.loadDocuments()
  },
  beforeDestroy() {
    this.requestSequence += 1
  },
  methods: {
    async loadDocuments() {
      const sequence = this.requestSequence + 1
      this.requestSequence = sequence
      this.loading = true
      this.loadFailed = false
      this.documents = null
      const { userData } = this.$store.state.app
      if (!userData) {
        this.loading = false
        return
      }
      try {
        const language = this.$i18n.locale === 'th' ? 'th' : 'en'
        const res = await agentService.getAuthen(`agent-delegation/poy-details?language=${language}`, { preserveSession: true })
        if (sequence !== this.requestSequence || userData !== this.$store.state.app.userData) return
        if (res.status === 403) {
          this.$store.commit('app/UPDATE_POY_DETAILS_RIGHTS', {
            canManagePoyDetails: this.$store.getters['app/canManagePoyDetails'],
          })
          this.$router.replace({ name: 'misc-not-authorized' })
          return
        }
        if (res.status !== 200 || !res.data || !res.data.common || (!res.data.callback && !res.data.api)) {
          this.loadFailed = true
          return
        }
        this.$store.commit('app/UPDATE_POY_DETAILS_RIGHTS', res.data)
        this.documents = res.data
      } catch (error) {
        if (sequence === this.requestSequence && userData === this.$store.state.app.userData) this.loadFailed = true
      } finally {
        if (sequence === this.requestSequence && userData === this.$store.state.app.userData) this.loading = false
      }
    },
    onCopy() {
      this.$toast({
        component: ToastificationContent,
        props: { title: this.$t('poyDetailsDocs.copied'), icon: 'CheckIcon', variant: 'success' },
      })
    },
    onCopyError() {
      this.$toast({
        component: ToastificationContent,
        props: { title: this.$t('poyDetailsDocs.copyFailed'), icon: 'AlertCircleIcon', variant: 'danger' },
      })
    },
  },
}
</script>
